import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import { Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { formatPrice } from '@/utils/Product';

import { localCart } from '../../../../atoms/LocalCart';
import { localUserID, user } from '../../../../atoms/user';
import {
  addToCart,
  addToRecentlyViewed,
  getCartCount,
} from '../../../../services/Cart';
import { AddToCartProps } from '../../../../utils/interfaces/cart';
import { BookItems } from '../../../../utils/interfaces/Products/books';
import { User } from '../../../../utils/interfaces/User';
import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../../shared/formateErrorObject';
import { notify } from '../../../shared/notify';

const { Text } = Typography;

interface ProductDetailsCardProps {
  productData: BookItems;
  isCourses?: boolean;
}

const BookCard: React.FC<ProductDetailsCardProps> = ({ productData }) => {
  const [showMore, setShowMore] = useState(false);
  const router = useRouter();
  const screens = useBreakpoint();
  const userData: User | undefined = useRecoilValue(user);

  const [loading, setLoading] = useState({ value: false, id: 0 });
  const localUserId = useRecoilValue(localUserID);
  const [, setLocalCartCount] = useRecoilState(localCart);
  const [, setLocalUserId] = useRecoilState<string>(localUserID);

  const stripHTMLTags = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const textContent = doc.body?.textContent || '';
    return screens.xl ? textContent : textContent.slice(0, 200);
  };

  const truncatedText = showMore
    ? stripHTMLTags(productData?.desc)
    : stripHTMLTags(productData?.desc).slice(0, 120) +
      (productData?.desc.length > 150 ? '...' : '');

  const handleAddDataToCart = async (payload: AddToCartProps) => {
    try {
      setLoading({ value: true, id: payload.id });
      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      handleCartCount();
      router.push('/cart');
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading({ value: false, id: payload.id });
    }
  };

  const handleCartCount = async () => {
    try {
      const data = await getCartCount({});
      setLocalCartCount(data.count);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  useEffect(() => {
    const setUserIdInLocalStorage = async () => {
      const existingUserId = localStorage.getItem('userId');

      if (!existingUserId) {
        const userId = uuidv4();

        localStorage.setItem('userId', userId);
        setLocalUserId(userId);
      }
      setLocalUserId(existingUserId as string);
    };

    setUserIdInLocalStorage();
  }, []);

  return (
    <div className='w-full rounded-md cursor-pointer flex flex-col transition-duration-300'>
      <div className='h-full  text-fadedWhite flex flex-col'>
        <div className='flex flex-col md:flex-row gap-y-4 items-center h-full gap-x-6'>
          <div className='flex gap-x-3'>
            <div className='flex justify-center min-w-[93px] md:min-w-[152px]'>
              <div
                className='lg:w-full flex justify-center h-full '
                style={{ display: 'flex' }}
              >
                <Image
                  src={productData?.bookImage}
                  alt={productData?.heading}
                  width={screens.md ? 150 : 93}
                  height={screens.md ? 220 : 136}
                  className='object-contain max-h-[136px] md:max-h-[220px] rounded-md md:rounded-lg'
                />
              </div>
            </div>
            <ShouldRender check={!screens.md}>
              <div>
                <div className='flex flex-col  h-full w-full'>
                  <div className='text-sm md:text-base lg:text-xl font-bold'>
                    {productData.heading}
                  </div>

                  <ShouldRender check={productData?.desc}>
                    <div className='flex md:h-24 overflow-auto customScrollBar mt-1 flex-col gap-y-1 lg:gap-y-3 '>
                      <div className='flex text-xs lg:text-sm'>
                        <Text className='text-textLightGray text-xs md:text-sm '>
                          {truncatedText}
                          <ShouldRender check={screens.xl}>
                            {productData?.desc.length > 150 && (
                              <span
                                className='underline pl-2 cursor-pointer text-primary hover:text-darkPrimary'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowMoreClick();
                                }}
                              >
                                {showMore ? 'Show less' : 'Show more'}
                              </span>
                            )}
                          </ShouldRender>
                        </Text>
                      </div>
                    </div>
                  </ShouldRender>
                </div>
              </div>
            </ShouldRender>
          </div>

          <div className='h-full w-full'>
            <div className='flex flex-col justify-between h-full w-full gap-y-2'>
              <ShouldRender check={screens.md}>
                <div className='flex flex-col  h-full w-full'>
                  <div className='text-sm md:text-base lg:text-xl font-bold'>
                    {productData.heading}
                  </div>

                  <ShouldRender check={productData?.desc}>
                    <div className='flex md:h-24 overflow-auto customScrollBar mt-1 flex-col gap-y-1 lg:gap-y-3 '>
                      <div className='flex text-xs lg:text-sm'>
                        <Text className='text-textLightGray text-xs md:text-sm '>
                          {truncatedText}
                          <ShouldRender check={screens.xl}>
                            {productData?.desc.length > 150 && (
                              <span
                                className='underline pl-2 cursor-pointer text-primary hover:text-darkPrimary'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowMoreClick();
                                }}
                              >
                                {showMore ? 'Show less' : 'Show more'}
                              </span>
                            )}
                          </ShouldRender>
                        </Text>
                      </div>
                    </div>
                  </ShouldRender>
                </div>
              </ShouldRender>

              <div className='flex gap-y-2 gap-x-2 md:gap-x-3 w-full justify-center '>
                <ButtonDefault
                  size={!screens.xs ? 1 : 0}
                  isWidthFull={true}
                  rounded={false}
                  roundedSm={true}
                  variant={
                    productData.is_available_soft_copy
                      ? ButtonVariants.GRAY
                      : ButtonVariants.UNSTYLED
                  }
                  available={productData.is_available_soft_copy}
                  enableScaling={false}
                  loading={
                    productData.softCopyId === loading.id && loading.value
                  }
                  disabled={
                    productData.softCopyId === loading.id && loading.value
                  }
                  onClick={(e) => {
                    e.stopPropagation();

                    handleAddDataToCart({
                      id: productData.softCopyId as number,
                      temp_user_id: localUserId,
                      quantity: 1,
                      trackingData: {
                        productData: {
                          price: Number(productData.softCopyPrice),
                          id: productData.softCopyId as number,
                          heading: productData.heading as string,
                          quantity: productData.quantity as number,
                          currency: productData.i18n_attributes
                            ?.currency as string,
                        },
                        userData,
                        pathname: router.pathname,
                        productType: 'digitalcopy',
                        type: 'books',
                      },
                    });
                  }}
                  unavailableText='Digital Copy'
                  className={clsx(
                    productData?.is_available_soft_copy &&
                      'bg-[#1C1C1C] border-none',
                    'w-full text-center text-sm group  ',
                  )}
                >
                  <div className='flex justify-between items-center w-full  px-1 md:px-3 lg:px-1 xl:px-3 py-1 '>
                    <span className='flex flex-col justify-start md:gap-y-1'>
                      <p className='w-full text-start font-semibold uppercase text-[10px] md:text-xs'>
                        Digital Copy
                      </p>
                      <span className='flex xxxs:gap-x-1 md:gap-x-2 items-baseline pl-1'>
                        <p className='line-through text-[#77777C] text-xs  md:text-sm font-medium whitespace-nowrap'>
                          {productData?.currency}
                          {formatPrice(
                            productData?.actualPriceSoftCopy as number,
                          )}
                        </p>
                        <p className='text-sm font-bold whitespace-nowrap'>
                          {productData?.currency}
                          {formatPrice(productData?.softCopyPrice)}
                        </p>
                        <ShouldRender
                          check={productData.softCopyId !== loading.id}
                        >
                          <ShouldRender check={screens.xl || !screens.lg}>
                            {productData?.actualPriceSoftCopy &&
                            productData?.softCopyPrice ? (
                              <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-[10px] md:text-sm items-center'>
                                {Math.round(
                                  ((productData.actualPriceSoftCopy -
                                    Number(productData.softCopyPrice)) /
                                    productData.actualPriceSoftCopy) *
                                    100,
                                )}
                                %{' '}
                                <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                              </p>
                            ) : null}
                          </ShouldRender>
                        </ShouldRender>
                      </span>
                    </span>
                  </div>
                </ButtonDefault>

                <ButtonDefault
                  size={!screens.xs ? 1 : 0}
                  isWidthFull={true}
                  rounded={false}
                  roundedSm={true}
                  variant={
                    productData.is_available_hard_copy
                      ? ButtonVariants.WHITE
                      : ButtonVariants.UNSTYLED
                  }
                  enableScaling={false}
                  loading={
                    productData.hardCopyId === loading.id && loading.value
                  }
                  unavailableText='Hard Copy'
                  disabled={
                    productData.hardCopyId === loading.id && loading.value
                  }
                  onClick={(e) => {
                    e.stopPropagation();

                    handleAddDataToCart({
                      id: productData.hardCopyId as number,
                      temp_user_id: localUserId.toString(),
                      quantity: 1,
                      trackingData: {
                        productData: {
                          price: Number(productData.hardCopyPrice),
                          id: productData.hardCopyId as number,
                          heading: productData.heading as string,
                          quantity: productData.quantity as number,
                          currency: productData.i18n_attributes
                            ?.currency as string,
                        },
                        userData,
                        pathname: router.pathname,
                        productType: 'hardcopy',
                        type: 'books',
                      },
                    });
                  }}
                  available={productData.is_available_hard_copy}
                  className='w-full text-center text-sm group'
                >
                  <div className='flex justify-between items-center w-full  px-1 md:px-3 lg:px-1 xl:px-3 py-1 '>
                    <span className='flex flex-col justify-start md:gap-y-1'>
                      <p className='w-full text-start font-semibold uppercase text-[10px] md:text-xs'>
                        Hard Copy
                      </p>
                      <span className='flex xxxs:gap-x-1 md:gap-x-2 items-baseline pl-1'>
                        <p className='line-through text-[#77777C] text-xs  md:text-sm font-medium whitespace-nowrap'>
                          {productData?.currency}
                          {formatPrice(
                            productData?.actualPriceHardCopy as number,
                          )}
                        </p>
                        <p className='text-sm font-bold whitespace-nowrap'>
                          {productData?.currency}
                          {formatPrice(productData?.hardCopyPrice)}
                        </p>
                        <ShouldRender
                          check={productData.hardCopyId !== loading.id}
                        >
                          <ShouldRender check={screens.xl || !screens.lg}>
                            {productData?.actualPriceHardCopy &&
                            productData?.hardCopyPrice ? (
                              <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-[10px] md:text-sm items-center'>
                                {Math.round(
                                  ((productData.actualPriceHardCopy -
                                    Number(productData.hardCopyPrice)) /
                                    productData.actualPriceHardCopy) *
                                    100,
                                )}
                                %{' '}
                                <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                              </p>
                            ) : null}
                          </ShouldRender>
                        </ShouldRender>
                      </span>
                    </span>
                  </div>
                </ButtonDefault>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookCard;
